import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaBan, FaCalendarAlt, FaChevronLeft, FaChevronRight, FaList, FaPlus } from "react-icons/fa";
import Table from "../../components/Table";
import AddDialog from "../../components/Modal/AddModal";
import TextInput from "../../components/Select/InputText";
import SelectInput from "../../components/Select/SelectInput";
import SearchableSelect from "../../components/Select/SearchableSelect";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";
import {
    addCourtBooking,
    BookingMember,
    cancelCourtBooking,
    Court,
    CourtBooking,
    getCourtBookingOptions,
    getCourtBookings,
} from "../../services/courtService";
import { showErrorToast, showSuccessToast } from "../../services/toastService";

const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getDateDay = (date: string) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "UTC" }).format(new Date(`${date}T00:00:00Z`));
};

const toDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const buildCalendarDays = (month: Date) => {
    const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay());

    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + index);
        return date;
    });
};

type BookingRow = {
    _id: string;
    courtName: string;
    customerName: string;
    mobile: string;
    date: string;
    time: string;
    status: string;
    notes: string;
    booking: CourtBooking;
};

const Bookings = () => {
    const { user } = useAuth();
    const canBook = hasPermission(user, "bookings.write");
    const [bookings, setBookings] = useState<CourtBooking[]>([]);
    const [courts, setCourts] = useState<Court[]>([]);
    const [members, setMembers] = useState<BookingMember[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [view, setView] = useState<"calendar" | "list">("calendar");
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });

    const loadData = async () => {
        try {
            const [bookingList, options] = await Promise.all([
                getCourtBookings(),
                getCourtBookingOptions(),
            ]);
            setBookings(bookingList);
            setCourts(options.courts);
            setMembers(options.members);
        } catch (error) {
            console.error("Failed to fetch bookings", error);
            showErrorToast("Failed to load bookings");
        }
    };

    useEffect(() => {
        if (user?.id) loadData();
    }, [user?.id]);

    const formik = useFormik({
        initialValues: {
            court: "",
            member: "",
            date: getLocalDate(),
            startTime: "",
            endTime: "",
            notes: "",
        },
        validationSchema: Yup.object({
            court: Yup.string().required("Court is required"),
            member: Yup.string().required("Customer is required"),
            date: Yup.string().required("Date is required"),
            startTime: Yup.string().required("Start time is required"),
            endTime: Yup.string()
                .required("End time is required")
                .test("after-start", "End time must be after start time", function (value) {
                    return !value || !this.parent.startTime || value > this.parent.startTime;
                }),
            notes: Yup.string().max(500, "Notes must be 500 characters or fewer"),
        }),
        onSubmit: async (values) => {
            try {
                const created = await addCourtBooking(values);
                setBookings((current) => [...current, created].sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`)));
                showSuccessToast("Court booked successfully");
                closeDialog();
            } catch (error: any) {
                showErrorToast(error?.response?.data?.error || "Failed to create booking");
            }
        },
    });

    const closeDialog = () => {
        setDialogOpen(false);
        formik.resetForm({ values: { ...formik.initialValues, date: getLocalDate() } });
    };

    const selectedCourt = courts.find((court) => court._id === formik.values.court);
    const selectedDay = getDateDay(formik.values.date);
    const availableTimes = selectedCourt?.availability?.filter((slot) => slot.day === selectedDay) || [];

    const memberOptions = useMemo(() => members.map((member) => ({
        value: member._id,
        label: `${member.firstName} ${member.lastName}${member.mobile ? ` — ${member.mobile}` : ""}`,
    })), [members]);

    const rows = useMemo<BookingRow[]>(() => bookings.map((booking) => ({
        _id: booking._id,
        courtName: booking.court?.name || "Deleted court",
        customerName: booking.member ? `${booking.member.firstName} ${booking.member.lastName}`.trim() : "Deleted customer",
        mobile: booking.member?.mobile || "-",
        date: booking.date,
        time: `${booking.startTime} – ${booking.endTime}`,
        status: booking.status,
        notes: booking.notes || "-",
        booking,
    })), [bookings]);

    const calendarDays = useMemo(() => buildCalendarDays(calendarMonth), [calendarMonth]);
    const bookedByDate = useMemo(() => bookings.reduce<Record<string, CourtBooking[]>>((result, booking) => {
        if (booking.status !== "Booked") return result;
        if (!result[booking.date]) result[booking.date] = [];
        result[booking.date].push(booking);
        return result;
    }, {}), [bookings]);

    const openBookingForDate = (date: Date) => {
        if (!canBook || toDateKey(date) < getLocalDate()) return;
        formik.setFieldValue("date", toDateKey(date));
        setDialogOpen(true);
    };

    const handleCancel = async (booking: CourtBooking) => {
        if (!window.confirm("Cancel this court booking?")) return;
        try {
            const updated = await cancelCourtBooking(booking._id);
            setBookings((current) => current.map((item) => item._id === updated._id ? updated : item));
            showSuccessToast("Booking cancelled");
        } catch (error: any) {
            showErrorToast(error?.response?.data?.error || "Failed to cancel booking");
        }
    };

    const columns = [
        { Header: "Court", accessor: "courtName" },
        { Header: "Customer", accessor: "customerName" },
        { Header: "Mobile", accessor: "mobile" },
        { Header: "Date", accessor: "date" },
        { Header: "Time", accessor: "time" },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }: { value: string }) => (
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${value === "Booked" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {value}
                </span>
            ),
        },
        { Header: "Notes", accessor: "notes" },
        ...(canBook ? [{
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }: any) => row.original.booking.status === "Booked" ? (
                <button
                    type="button"
                    onClick={() => handleCancel(row.original.booking)}
                    className="inline-flex items-center gap-1 text-red-600 hover:text-red-800"
                    aria-label="Cancel booking"
                    title="Cancel booking"
                >
                    <FaBan /> <span>Cancel</span>
                </button>
            ) : <span className="text-gray-400">—</span>,
        }] : []),
    ];

    return (
        <div className="w-full p-3 md:p-6">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Bookings</h1>
                    <p className="mt-1 text-sm text-gray-500">Assign a customer to an available court and time.</p>
                </div>
                <button
                    type="button"
                    onClick={() => setDialogOpen(true)}
                    disabled={!canBook}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#ac120c] px-4 py-2 text-sm font-semibold text-white hover:bg-[#8f0f0a] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <FaPlus /> New Booking
                </button>
            </div>

            <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                <button
                    type="button"
                    onClick={() => setView("calendar")}
                    className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${view === "calendar" ? "bg-white text-[#ac120c] shadow-sm" : "text-gray-600"}`}
                >
                    <FaCalendarAlt /> Calendar
                </button>
                <button
                    type="button"
                    onClick={() => setView("list")}
                    className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${view === "list" ? "bg-white text-[#ac120c] shadow-sm" : "text-gray-600"}`}
                >
                    <FaList /> List
                </button>
            </div>

            {view === "calendar" ? (
                <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                        <button
                            type="button"
                            onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
                            className="rounded-md border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
                            aria-label="Previous month"
                        >
                            <FaChevronLeft />
                        </button>
                        <div className="text-center">
                            <h2 className="text-lg font-bold">
                                {calendarMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                            </h2>
                            <button
                                type="button"
                                onClick={() => {
                                    const now = new Date();
                                    setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                                }}
                                className="text-xs font-semibold text-[#ac120c] hover:underline"
                            >
                                Today
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
                            className="rounded-md border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
                            aria-label="Next month"
                        >
                            <FaChevronRight />
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <div className="min-w-[760px]">
                            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-center text-xs font-bold uppercase tracking-wide text-gray-500">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                    <div key={day} className="px-2 py-3">{day}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7">
                                {calendarDays.map((date) => {
                                    const dateKey = toDateKey(date);
                                    const dayBookings = bookedByDate[dateKey] || [];
                                    const isCurrentMonth = date.getMonth() === calendarMonth.getMonth();
                                    const isToday = dateKey === getLocalDate();
                                    const isPast = dateKey < getLocalDate();
                                    return (
                                        <button
                                            key={dateKey}
                                            type="button"
                                            onClick={() => openBookingForDate(date)}
                                            disabled={!canBook || isPast}
                                            className={`min-h-[120px] border-b border-r border-gray-200 p-2 text-left align-top transition-colors ${isCurrentMonth ? "bg-white" : "bg-gray-50"} ${canBook && !isPast ? "hover:bg-red-50" : "cursor-default"}`}
                                        >
                                            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${isToday ? "bg-[#ac120c] text-white" : isCurrentMonth ? "text-gray-800" : "text-gray-400"}`}>
                                                {date.getDate()}
                                            </span>
                                            <div className="mt-1 space-y-1">
                                                {dayBookings.slice(0, 3).map((booking) => (
                                                    <div key={booking._id} className="truncate rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                                                        {booking.startTime} {booking.court?.name || "Court"}
                                                    </div>
                                                ))}
                                                {dayBookings.length > 3 && (
                                                    <div className="px-1 text-xs font-semibold text-gray-500">+{dayBookings.length - 3} more</div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                        Click an available date to create a booking. Existing bookings are shown in red.
                    </div>
                </section>
            ) : (
                <Table columns={columns} data={rows} pageSize={10} />
            )}

            <AddDialog title="Book a Court" isOpen={dialogOpen} onClose={closeDialog} maxWidth="max-w-2xl">
                <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <SelectInput
                        label="Court"
                        name="court"
                        value={formik.values.court}
                        onChange={formik.handleChange}
                        options={courts.map((court) => ({ value: court._id, label: court.name }))}
                    />
                    {formik.touched.court && formik.errors.court && <p className="text-sm text-red-500">{formik.errors.court}</p>}

                    <SearchableSelect
                        label="Customer"
                        name="member"
                        value={formik.values.member}
                        options={memberOptions}
                        placeholder="Search by customer name or mobile"
                        onChange={(name, value) => formik.setFieldValue(name, value)}
                        onBlur={(name) => formik.setFieldTouched(name, true)}
                    />
                    {formik.touched.member && formik.errors.member && <p className="text-sm text-red-500">{formik.errors.member}</p>}

                    <TextInput label="Date" type="date" name="date" min={getLocalDate()} value={formik.values.date} onChange={formik.handleChange} />
                    {formik.touched.date && formik.errors.date && <p className="text-sm text-red-500">{formik.errors.date}</p>}

                    {selectedCourt && formik.values.date && (
                        <div className={`rounded border px-3 py-2 text-sm ${availableTimes.length ? "border-blue-200 bg-blue-50 text-blue-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                            <span className="font-semibold">{selectedDay} availability:</span>{" "}
                            {availableTimes.length
                                ? availableTimes.map((slot) => `${slot.startTime}–${slot.endTime}`).join(", ")
                                : "This court is not available on this day."}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                            <TextInput label="Start Time" type="time" name="startTime" value={formik.values.startTime} onChange={formik.handleChange} />
                            {formik.touched.startTime && formik.errors.startTime && <p className="mt-1 text-sm text-red-500">{formik.errors.startTime}</p>}
                        </div>
                        <div>
                            <TextInput label="End Time" type="time" name="endTime" value={formik.values.endTime} onChange={formik.handleChange} />
                            {formik.touched.endTime && formik.errors.endTime && <p className="mt-1 text-sm text-red-500">{formik.errors.endTime}</p>}
                        </div>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label htmlFor="notes" className="text-sm font-semibold">Notes (optional)</label>
                        <textarea
                            id="notes"
                            name="notes"
                            rows={3}
                            value={formik.values.notes}
                            onChange={formik.handleChange}
                            className="rounded border px-3 py-2"
                            placeholder="Booking details"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={!courts.length || !members.length}
                        className="rounded-lg bg-[#ac120c] px-5 py-2 text-white hover:bg-[#8f0f0a] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Confirm Booking
                    </button>
                </form>
            </AddDialog>
        </div>
    );
};

export default Bookings;
